import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { loginUser } from "./authSlice";
import { useNavigate, Link } from "react-router-dom";
import {
  Box,
  Button,
  TextField,
  Typography,
  IconButton,
  InputAdornment,
  Paper,
} from "@mui/material";
import { Visibility, VisibilityOff, ArrowBack as ArrowBackIcon } from "@mui/icons-material";

const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [isEmailValid, setIsEmailValid] = useState(true);
  const [passwordError, setPasswordError] = useState("");
  const [isPasswordValid, setIsPasswordValid] = useState(true);

  // Email validation function
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Handle email change with validation
  const handleEmailChange = (e) => {
    const newEmail = e.target.value;
    setEmail(newEmail);

    // Clear errors when user starts typing
    if (emailError) {
      setEmailError("");
      setIsEmailValid(true);
    }
    if (error) {
      setError("");
    }

    if (newEmail === "") {
      setEmailError("");
      setIsEmailValid(true);
    } else if (!validateEmail(newEmail)) {
      setEmailError("Please enter a valid email address");
      setIsEmailValid(false);
    } else {
      setEmailError("");
      setIsEmailValid(true);
    }
  };

  // Handle password change with validation
  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    setPassword(newPassword);

    // Clear errors when user starts typing
    if (passwordError) {
      setPasswordError("");
      setIsPasswordValid(true);
    }
    if (error) {
      setError("");
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");

    // Validate email before submission
    if (!isEmailValid || email === "") {
      setEmailError("Please enter a valid email address");
      setIsEmailValid(false);
      return;
    }

    // Validate password
    if (!password || password.trim() === "") {
      setPasswordError("Please enter your password");
      setIsPasswordValid(false);
      return;
    }

    try {
      const result = await dispatch(loginUser({ email, password }));
      if (result.meta.requestStatus === "fulfilled") {
        navigate("/renewals");
      } else {
        throw result.error;
      }
    } catch (err) {
      if (err.code === "auth/invalid-login-credentials") {
        setError("Incorrect email or password");
      } else if (err.code === "auth/invalid-email") {
        setEmailError("Please enter a valid email address");
        setIsEmailValid(false);
      } else {
        setError(err.message || "Login failed");
      }
    }
  };

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
      bgcolor="#f0f2f5"
      sx={{ px: { xs: 2, sm: 0 } }}
    >
      <Paper
        elevation={0}
        sx={{
          padding: { xs: 2, sm: 3 },
          width: { xs: '100%', sm: 400, md: 440 },
          maxWidth: '440px',
          borderRadius: 2,
          position: 'relative'
        }}
      >
        <IconButton
          onClick={() => navigate('/login')}
          sx={{
            position: 'absolute',
            left: 16,
            top: 16,
            color: '#666'
          }}
        >
          <ArrowBackIcon />
        </IconButton>

        {/* Logo Icon */}
        <Box display="flex" justifyContent="center" mb={{ xs: 1.5, sm: 2 }}>
          <Box
            sx={{
              fontSize: { xs: '60px', sm: '72px' },
              fontWeight: '900',
              color: '#0051C3',
              fontFamily: '"Roboto Slab", "Rockwell", "Courier New", "Courier", monospace',
              lineHeight: 1,
              letterSpacing: '0.02em',
              textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
            }}
          >
            P
          </Box>
        </Box>

        <Typography
          variant="h4"
          align="center"
          gutterBottom
          sx={{
            fontWeight: 'bold',
            fontSize: { xs: '1.75rem', sm: '2.125rem' }
          }}
        >
          Policytrackr
        </Typography>
        <Typography
          variant="subtitle1"
          align="center"
          gutterBottom
          sx={{
            fontSize: { xs: '0.9rem', sm: '1rem' },
            mb: { xs: 2, sm: 1 }
          }}
        >
          Built for insurance agents & agencies
        </Typography>

        <Box component="form" onSubmit={handleLogin} mt={{ xs: 1, sm: 1 }}>
          <TextField
            fullWidth
            label="Email"
            variant="outlined"
            margin="dense"
            value={email}
            onChange={handleEmailChange}
            required
            error={!isEmailValid}
            helperText={emailError}
            sx={{
              "& .MuiFormHelperText-root": {
                marginLeft: 0,
                marginRight: 0,
              },
              "& .MuiInputBase-root": {
                fontSize: { xs: '0.9rem', sm: '1rem' }
              },
              "& .MuiInputLabel-root": {
                "&.MuiInputLabel-shrink": {
                  fontSize: { xs: '0.75rem', sm: '0.85rem' },
                  transform: { xs: 'translate(14px, -9px) scale(0.75)', sm: 'translate(14px, -9px) scale(0.75)' },
                }
              },
              "& .MuiOutlinedInput-notchedOutline": {
                "& legend": {
                  fontSize: { xs: '0.6rem', sm: '0.65rem' },
                }
              }
            }}
          />

          <TextField
            fullWidth
            label="Password"
            variant="outlined"
            margin="dense"
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={handlePasswordChange}
            required
            error={!isPasswordValid}
            helperText={passwordError}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    size={window.innerWidth < 600 ? "small" : "medium"}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiFormHelperText-root": {
                marginLeft: 0,
                marginRight: 0,
              },
              "& .MuiInputBase-root": {
                fontSize: { xs: '0.9rem', sm: '1rem' }
              },
              "& .MuiInputLabel-root": {
                "&.MuiInputLabel-shrink": {
                  fontSize: { xs: '0.75rem', sm: '0.85rem' },
                  transform: { xs: 'translate(14px, -9px) scale(0.75)', sm: 'translate(14px, -9px) scale(0.75)' },
                }
              },
              "& .MuiOutlinedInput-notchedOutline": {
                "& legend": {
                  fontSize: { xs: '0.6rem', sm: '0.65rem' },
                }
              }
            }}
          />

          <Box display="flex" justifyContent="flex-end" mt={{ xs: 0.5, sm: 1 }}>
            <Button
              component={Link}
              to="/forgot-password"
              variant="text"
              sx={{
                color: '#0051C3',
                fontWeight: 500,
                fontSize: { xs: '0.85rem', sm: '0.875rem' },
                backgroundColor: 'transparent',
                '&:hover': {
                  backgroundColor: 'transparent',
                  textDecoration: 'underline',
                  fontWeight: 600,
                }
              }}
            >
              Forgot Password?
            </Button>
          </Box>

          <Button
            fullWidth
            type="submit"
            variant="contained"
            color="primary"
            sx={{
              mt: { xs: 1, sm: 1.5 },
              py: { xs: 1, sm: 1.2 },
              fontSize: { xs: '0.9rem', sm: '1rem' },
              backgroundColor: '#0051C3'
            }}
          >
            Login
          </Button>

          <Box mt={3} textAlign="center">
            <Typography variant="body2" sx={{ color: '#666' }}>
              Want to login as admin?{" "}
              <Typography
                component={Link}
                to="/login-admin"
                sx={{
                  color: '#0051C3',
                  fontWeight: 500,
                  textDecoration: 'none',
                  '&:hover': {
                    textDecoration: 'underline',
                    fontWeight: 600
                  }
                }}
              >
                Click here
              </Typography>
            </Typography>
          </Box>

          {error && (
            <Typography
              color="error"
              mt={2}
              textAlign="center"
              sx={{ fontSize: { xs: '0.85rem', sm: '1rem' } }}
            >
              {error}
            </Typography>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default Login;